import { RequestHeaders, ResponseResultType } from '../../../config/api';
import { publicApiClient } from '../../../config/overrides/api';
import { composeUrl } from '../utils';
import { RegistrationStatus } from '../../constants';
import {
  AppSettings,
  ATTENDEE,
  CONFIGURATION,
  EVENT,
  EventPublicDetailsResponseType,
  EXPORT,
  GOOGlE,
  GoogleCalendarResponseType,
  ICS,
  MEETING,
  MEETING_LINK,
  PANELIST,
  PARTICIPANT,
  PREREGISTER,
  PRESENTER,
  PresenterParticipantDetailsResponseType,
  PROPERTIES,
  PublicMeetingDetailsResponseType,
  PublicSessionMeetingDetails,
  REGISTER,
  RegistrationSubscriptionParams,
  MeetingLinkPanelistPreregisterResponse,
  PROCESSING_RESULT,
  RESEND_CODE,
  SpeakerListResponseType,
  PrivateMeetingSessionDetailsListResponseType,
  REGISTRATION_CONFIGURATION,
  AttendeeProcessingUrlResponse,
  AttendeeOneWayRegistrationErrorResponse,
  PassportAuthTokens,
  AttendeeRegistrationRequest,
  CalendarInviteResquest,
  AccountInfoResponseType,
  UrlRedirectionResponseType,
  SSOAuthResponse,
  SamlSsoInitiateResponse
} from '../index';
import { ATTENDEE_TOKEN_KEY, BYPASS_TOKEN_KEY, TOKEN, TYPE } from '../../constants';
import { getAccessToken, getRefreshToken } from '../../auth';

const API_PREFIX = 'public';
export const composePublicUrl = (...args: string[]): string => composeUrl(API_PREFIX, ...args);

export function fetchPublicPresenterDetails(...path: string[]): ResponseResultType<PresenterParticipantDetailsResponseType> {
  return publicApiClient.GET(composePublicUrl(PRESENTER, ...path));
}

export function fetchPublicParticipantDetails(...path: string[]): ResponseResultType<PresenterParticipantDetailsResponseType> {
  return publicApiClient.GET(composePublicUrl(PARTICIPANT, ...path));
}

export function fetchPublicMeetingDetails(meetingPath: string, passcode: string): ResponseResultType<PublicMeetingDetailsResponseType> {
  return publicApiClient.GET(composePublicUrl(MEETING, meetingPath), { headers: { [RequestHeaders.Passcode]: btoa(passcode) }});
}

export function fetchPublicMeetingDetailsWithBypassToken(meetingPath: string, bypassToken: string): ResponseResultType<PublicMeetingDetailsResponseType> {
  return publicApiClient.GET(`${composePublicUrl(MEETING, meetingPath)}?${BYPASS_TOKEN_KEY}=${bypassToken}`);
}

export function fetchPublicMeetingDetailsWithAttendeeToken(meetingPath: string, attendeeToken: string): ResponseResultType<PublicMeetingDetailsResponseType> {
  return publicApiClient.GET(`${composeUrl(API_PREFIX, MEETING, meetingPath)}?${ATTENDEE_TOKEN_KEY}=${attendeeToken}`);
}

export function fetchPublicMeetingLinkDetails(meetingLinkPath: string, token: string): ResponseResultType<PublicMeetingDetailsResponseType> {
  return publicApiClient.GET(`${composePublicUrl(MEETING_LINK, meetingLinkPath)}?${BYPASS_TOKEN_KEY}=${token}`);
}

const withJoinMethodId = (query: string, joinMethodId?: number): string => {
  if (joinMethodId == null) {
    return query;
  }
  return `${query}&joinMethodId=${joinMethodId}`;
};

export function fetchMeetingLinkPanelistPreregister(
  meetingLinkPath: string,
  token: string,
  deviceToken: string,
  joinMethodId?: number
): ResponseResultType<MeetingLinkPanelistPreregisterResponse> {
  return publicApiClient.GET(
    withJoinMethodId(
      `${composePublicUrl(MEETING_LINK, meetingLinkPath, PANELIST, PREREGISTER)}?${BYPASS_TOKEN_KEY}=${token}&deviceToken=${deviceToken}`,
      joinMethodId
    )
  );
}

export function registerMeetingLinkPanelist(
  meetingLinkPath: string,
  token: string,
  deviceToken: string,
  registrationId: string,
  joinMethodId?: number
): ResponseResultType<MeetingLinkPanelistPreregisterResponse> {
  return publicApiClient.POST(
    `${composePublicUrl(MEETING_LINK, meetingLinkPath, PANELIST, REGISTER)}?${BYPASS_TOKEN_KEY}=${token}`,
    {
      deviceToken,
      registrationId,
      ...(joinMethodId != null ? { joinMethodId } : {}),
    }
  );
}

export function fetchMeetingLinkPanelistProcessingResult(
  meetingLinkPath: string,
  token: string,
  registrationId: string,
  deviceToken: string,
  joinMethodId?: number,
): ResponseResultType<AttendeeProcessingUrlResponse> {
  return publicApiClient.GET(
    withJoinMethodId(
      `${composePublicUrl(MEETING_LINK, meetingLinkPath, PANELIST, PROCESSING_RESULT)}?${BYPASS_TOKEN_KEY}=${token}&registrationId=${registrationId}&deviceToken=${deviceToken}`,
      joinMethodId
    )
  );
}

export function fetchPublicEventDetailsBySubdomain(subdomain: string): ResponseResultType<EventPublicDetailsResponseType> {
  return publicApiClient.GET(composePublicUrl(EVENT, subdomain));
}

export function fetchPublicEventDetailsByAccount(accountId: number, eventPath: string): ResponseResultType<EventPublicDetailsResponseType> {
  return publicApiClient.GET(composePublicUrl(`account/${accountId}/event/${eventPath}`));
}

export function fetchPublicCustomFieldsByEventId(eventId: string): Promise<void> {
  return publicApiClient.GET(composePublicUrl(EVENT, eventId, REGISTRATION_CONFIGURATION));
}

export function fetchPublicTriageAnswerCustomFieldsByEventId(eventId: number, selectedTriageAnswer: string): Promise<void> {
  return publicApiClient.GET(`${composePublicUrl(EVENT, eventId.toString(), REGISTRATION_CONFIGURATION)}?formName=${encodeURIComponent(selectedTriageAnswer)}`);
}

export function requestAccessCode(eventId: string, email: string, registrationId?: string): Promise<void> {
  const payload = registrationId ? { email, registrationId } : { email };
  return publicApiClient.POST(composePublicUrl(EVENT, eventId, ATTENDEE, RESEND_CODE), payload);
}

export function fetchRequestAccessCodeParameters(eventId: string): Promise<{ registrationId: string, appSyncToken: string }> {
  return publicApiClient.GET(composePublicUrl(EVENT, eventId, ATTENDEE, RESEND_CODE));
}

export function registerAttendee(eventId: string, params: Record<string, unknown>, externalAttendeeId?: string): Promise<{ registrationId: string, appSyncToken: string, eventToken: string | undefined, registrationApprovalEnabled: boolean, registrationStatus?: RegistrationStatus }> {
  let url = composePublicUrl(EVENT, eventId, ATTENDEE, REGISTER);
  if (externalAttendeeId) {
    url += `?externalAttendeeId=${encodeURIComponent(externalAttendeeId)}`;
  }
  return publicApiClient.POST(url, { ...params });
}

export async function fetchOutlookCalendarFile(eventId: string, eventToken: string | undefined): Promise<Blob> {
  return await publicApiClient.GET(`${composePublicUrl(EVENT, eventId, EXPORT)}?${TYPE}=${ICS}&${TOKEN}=${eventToken}`);
}

export async function fetchGoogleCalendarLink(eventId: string, eventToken: string | undefined): Promise<GoogleCalendarResponseType> {
  return await publicApiClient.GET(`${composePublicUrl(EVENT, eventId, EXPORT)}?${TYPE}=${GOOGlE}&${TOKEN}=${eventToken}`);
}

export function fetchEventGeneralSessionList(...args: string[]): ResponseResultType<PublicSessionMeetingDetails> {
  return publicApiClient.GET(composePublicUrl(...args));
}

export function fetchOutlookCalendarFileByBypassToken(meetingPath: string, bypassToken: string): Promise<Blob> {
  return publicApiClient.GET(`${composePublicUrl(MEETING, meetingPath, EXPORT)}?${TYPE}=${ICS}&${BYPASS_TOKEN_KEY}=${bypassToken}`);
}

export function fetchGoogleCalendarLinkByBypassToken(meetingPath: string, bypassToken: string): Promise<GoogleCalendarResponseType> {
  return publicApiClient.GET(`${composePublicUrl(MEETING, meetingPath, EXPORT)}?${TYPE}=${GOOGlE}&${BYPASS_TOKEN_KEY}=${bypassToken}`);
}

export function fetchOutlookCalendarFileByAttendeeToken(meetingPath: string, attendeeToken: string): Promise<Blob> {
  return publicApiClient.GET(`${composePublicUrl(MEETING, meetingPath, EXPORT)}?${TYPE}=${ICS}&${ATTENDEE_TOKEN_KEY}=${attendeeToken}`);
}

export function fetchGoogleCalendarLinkByAttendeeToken(meetingPath: string, attendeeToken: string): Promise<GoogleCalendarResponseType> {
  return publicApiClient.GET(`${composePublicUrl(MEETING, meetingPath, EXPORT)}?${TYPE}=${GOOGlE}&${ATTENDEE_TOKEN_KEY}=${attendeeToken}`);
}

export function fetchOutlookCalendarFileByPasscode(meetingPath: string, passcode: string): Promise<Blob> {
  return publicApiClient.GET(`${composePublicUrl(MEETING, meetingPath, EXPORT)}?${TYPE}=${ICS}`,
    { headers: { [RequestHeaders.Passcode]: btoa(passcode) }});
}

export function fetchGoogleCalendarLinkByPasscode(meetingPath: string, passcode: string): Promise<GoogleCalendarResponseType> {
  return publicApiClient.GET(`${composePublicUrl(MEETING, meetingPath, EXPORT)}?${TYPE}=${GOOGlE}`,
    { headers: { [RequestHeaders.Passcode]: btoa(passcode) }});
}

export async function fetchPublicSpeakerList(...args: string[]): Promise<SpeakerListResponseType> {
  return await publicApiClient.GET(composePublicUrl(...args));
}

export async function fetchMeetingPublicSessionsList(...args: string[]): Promise<PrivateMeetingSessionDetailsListResponseType> {
  return await publicApiClient.GET(composePublicUrl(...args));
}

export async function fetchRegistrationSubscriptionParams(eventId: string, selectedTriageAnswer: string): Promise<RegistrationSubscriptionParams> {
  return await publicApiClient.GET(`${composePublicUrl(EVENT, eventId, ATTENDEE, PREREGISTER)}?formName=${encodeURIComponent(selectedTriageAnswer)}`);
}

export async function fetchApplicationSettings(): Promise<AppSettings> {
  return await publicApiClient.GET(composePublicUrl(CONFIGURATION, PROPERTIES));
}

export async function fetchAttendeeProcessedResultUrlByRegistrationParams(registrationId: string, appSyncToken: string): Promise<AttendeeProcessingUrlResponse> {
  return await publicApiClient.GET(
    `${composeUrl('attendee/processing-result')}?registrationId=${registrationId}`,
    { headers: { [RequestHeaders.AppSyncToken]: appSyncToken }}
  );
}

export async function fetchFileDownloadUrlByPublicIdentifier(publicIdentifier: string): Promise<string> {
  return await publicApiClient.GET(`${composePublicUrl('asset', publicIdentifier, 'signed-url')}`);
}

export async function initiateAttendeeRegistration(params: AttendeeRegistrationRequest): Promise<void> {
  return await publicApiClient.GET(
    `${composeUrl('attendee/one-way-register/initiate')}?registrationId=${params.registrationId}&registrationApprovalEnabled=${params.registrationApprovalEnabled}`,
    { headers: { [RequestHeaders.AppSyncToken]: params.appSyncToken }}
  );
}

export async function initiateHubAttendeeRegistration(params: AttendeeRegistrationRequest): Promise<void> {
  return await publicApiClient.GET(
    `${composeUrl('attendee/one-way-register/hub/initiate')}?registrationId=${params.registrationId}&registrationApprovalEnabled=${params.registrationApprovalEnabled}`,
    { headers: { [RequestHeaders.AppSyncToken]: params.appSyncToken }}
  );
}

export async function fetchAttendeeOneWayRegistrationError(registrationId: string, attendeeId: string, appSyncToken: string): Promise<AttendeeOneWayRegistrationErrorResponse> {
  return await publicApiClient.GET(
    `${composeUrl('attendee/one-way-register/redirect')}?registrationId=${registrationId}&redirectId=${attendeeId}`,
    { headers: { [RequestHeaders.AppSyncToken]: appSyncToken }}
  );
}

export async function getAuthToken(authorization: string): Promise<PassportAuthTokens> {
  return await publicApiClient.GET(
    `${composeUrl('auth/jwt')}`,
    { headers: { [RequestHeaders.Authorization]: `Basic ${authorization}` }}
  );
}

export async function refreshToken(): Promise<PassportAuthTokens> {
  return publicApiClient.POST(composeUrl('auth/jwt'), {
    accessToken: getAccessToken(),
    refreshToken: getRefreshToken(),
  });
}

export async function verifyMagicLinkToken(authorization: string): Promise<PassportAuthTokens> {
  return publicApiClient.POST(composeUrl('auth/magic-link-token/verify'),
    {},
    {
      headers: { [RequestHeaders.Authorization]: authorization }
    }
  );
}

export async function sendCalendarInvite(attendeeInfo: CalendarInviteResquest): Promise<void> {
  return await publicApiClient.POST(composePublicUrl('attendee/send-calendar-invite'), attendeeInfo);
}

export async function fetchAccountInfoBySubdomain(subdomain: string): Promise<AccountInfoResponseType> {
  return await publicApiClient.GET(composePublicUrl(`account/${subdomain}`));
}

export async function fetchUrlRedirection(subdomain: string): Promise<UrlRedirectionResponseType> {
  return await publicApiClient.GET(composePublicUrl('url-redirection', subdomain));
}

export async function verifyMagicLinkTokenSSO(authorization: string): Promise<SSOAuthResponse> {
  return publicApiClient.GET(composeUrl('auth/sso/token-login'),
    {
      headers: { [RequestHeaders.Authorization]: authorization }
    }
  );
}

export async function getSSOAuthLogin(authorization: string): Promise<SSOAuthResponse> {
  return await publicApiClient.GET(
    `${composeUrl(`auth/sso/login`)}`,
    { headers: { [RequestHeaders.Authorization]: `Basic ${authorization}` }}
  );
}

export async function getSSOCallback(code: string, state: string): Promise<PassportAuthTokens> {
  return await publicApiClient.GET(
    `${composeUrl(`auth/sso/callback`)}?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`,
  );
}

export async function getSamlSsoInitiate(clientId: string, redirectPath: string, subdomain: string): Promise<SamlSsoInitiateResponse> {
  const query = new URLSearchParams({
    client_id: clientId,
    redirect_path: redirectPath,
    subdomain
  });
  return publicApiClient.GET(`${composePublicUrl('sso/saml/initiate')}?${query.toString()}`);
}
